import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from './quiz/ConfirmDialog';
import { SoundMuteToggle } from './quiz/QuizAtoms';
import { playHover, playSelect } from '../utils/sound';
import logoUrl from '../assets/images/cleveroo-logo.png';
import './ReadingChallenge.css';

// ─────────────────────────────────────────────
// Types (mirrors TestTaking.tsx shapes)
// ─────────────────────────────────────────────

type RichTextLike = string | { text?: string } | null | undefined;

interface PassageShape {
  title?: string;
  content?: RichTextLike;
  wordCount?: number;
  readingLevel?: string;
  source?: string;
  genre?: string;
  author?: string;
}

interface AnswerOption {
  id?: string;
  label?: string;
  text: string;
  isCorrect?: boolean;
  value?: string;
}

interface Question {
  id: string;
  type: string;
  title: string;
  subject?: string;
  source_metadata?: {
    comprehensionSkill?: string;
    naplan?: { requiresManualRubric?: boolean; yearLevel?: number; sourceYear?: number };
  };
  content: {
    questionText?: string;
    question?: string;
    passage?: PassageShape;
    passageParagraph?: number;
    options?: AnswerOption[];
  };
  answers: { text: string; value: string }[];
  difficulty: string;
}

interface TestData {
  id: string;
  title: string;
  description?: string;
  subject?: string;
  difficulty?: string;
  time_limit_minutes?: number;
}

interface TestSession {
  sessionId: string;
  test: TestData;
  questions: Question[];
  timeLimit: number; // seconds
}

interface RCState {
  cur: number;
  // answers[i] = option id (string) or null
  answers: (string | null)[];
  flags: boolean[];
  seconds: number;
  done: boolean;
}

// ─────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────

export interface ReadingChallengeProps {
  testId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialSession?: any;
  onComplete: (result: any) => void;
  onCancel: () => void;
}

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

const HTML_TAG_RE = /<[a-z][\s\S]*>/i;

const richTextToString = (value: RichTextLike): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return (value as any).text || '';
};

const DIFF_CLASS: Record<string, string> = {
  easy: 'rc-tag--easy',
  medium: 'rc-tag--medium',
  hard: 'rc-tag--hard',
};
const DIFF_LABEL: Record<string, string> = {
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
};

const RING_LEN = 2 * Math.PI * 11;

function fmtTime(s: number) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m + ':' + String(sec).padStart(2, '0');
}

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

const ReadingChallenge: React.FC<ReadingChallengeProps> = ({
  testId,
  initialSession,
  onComplete: _onComplete,
  onCancel,
}) => {
  const { user: authUser } = useAuth();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

  const getCurrentUser = () => {
    if (authUser) return authUser;
    try {
      const s = localStorage.getItem('cleveroo_user');
      if (s) return JSON.parse(s);
    } catch (e) {}
    return null;
  };
  const currentUser = getCurrentUser();

  // ── Loading / error state ──
  const [session, setSession] = useState<TestSession | null>((initialSession as TestSession) || null);
  const [loading, setLoading] = useState(!initialSession);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ── Result overlay ──
  const [result, setResult] = useState<{ score: number; correct: number; total: number; timeUsed: number } | null>(null);

  // ── Toast ──
  const [toastMsg, setToastMsg] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Test start time (for submit body) ──
  const testStartTimeRef = useRef<Date | null>(null);

  // ── Refs for DOM interactions ──
  const passageBodyRef = useRef<HTMLDivElement>(null);
  const passageRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLElement | null)[]>([]);
  const ioRef = useRef<IntersectionObserver | null>(null);

  // ── localStorage key ──
  const LS_KEY = `cleveroo_reading_${testId}`;

  // ────────────────────────────────────────────
  // STATE (answers, flags, timer, current index)
  // ────────────────────────────────────────────
  const loadRCState = useCallback((total: number, timeLimit: number): RCState => {
    try {
      const s: RCState = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
      if (
        s &&
        Array.isArray(s.answers) &&
        s.answers.length === total &&
        Array.isArray(s.flags) &&
        s.flags.length === total
      ) {
        return s;
      }
    } catch (e) {}
    return {
      cur: 0,
      answers: Array(total).fill(null),
      flags: Array(total).fill(false),
      seconds: timeLimit,
      done: false,
    };
  }, [LS_KEY]);

  const [rcState, setRCState] = useState<RCState>({
    cur: 0,
    answers: [],
    flags: [],
    seconds: 0,
    done: false,
  });

  // Save helper (write current state to LS)
  const saveStateRef = useRef<RCState>(rcState);
  useEffect(() => { saveStateRef.current = rcState; }, [rcState]);
  const persistState = useCallback((s: RCState) => {
    localStorage.setItem(LS_KEY, JSON.stringify(s));
  }, [LS_KEY]);

  // ────────────────────────────────────────────
  // FETCH SESSION (if not provided)
  // ────────────────────────────────────────────
  useEffect(() => {
    if (initialSession) {
      setSession(initialSession);
      setLoading(false);
      return;
    }
    if (!currentUser?.id) {
      setLoadError('User not authenticated');
      setLoading(false);
      return;
    }
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/api/students/${currentUser.id}/tests/${testId}/start`, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: false,
      })
      .then((res) => {
        if (res.data.success) {
          setSession(res.data.data);
        } else {
          setLoadError(res.data.error || 'Failed to start test');
        }
      })
      .catch((err) => {
        setLoadError(err.response?.data?.error || err.message || 'Failed to start test');
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId]);

  // ── Initialise RC state once session is available ──
  useEffect(() => {
    if (!session) return;
    const total = session.questions.length;
    const timeLimit = session.timeLimit;
    const s = loadRCState(total, timeLimit);
    setRCState(s);
    testStartTimeRef.current = new Date();
  }, [session, loadRCState]);

  // ────────────────────────────────────────────
  // PASSAGE
  // ────────────────────────────────────────────
  const passage: PassageShape | null = (() => {
    if (!session || session.questions.length === 0) return null;
    const q0 = session.questions[0];
    if (q0.content?.passage && (q0.content.passage.title || q0.content.passage.content)) {
      return q0.content.passage;
    }
    return null;
  })();

  const passageBody: string = passage ? richTextToString(passage.content) : '';
  const passageIsHtml = HTML_TAG_RE.test(passageBody);

  // Plain text → split on blank lines
  const passageParas: string[] = !passageIsHtml
    ? passageBody.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean)
    : [];

  // Byline
  const bylineParts: string[] = [];
  if (passage) {
    const genre = passage.genre || (passage.source && !passage.author ? undefined : undefined);
    if (genre) bylineParts.push(genre);
    else bylineParts.push('An informational text');
    if (passage.author) bylineParts.push(`by ${passage.author}`);
    else if (passage.source) bylineParts.push(`Source: ${passage.source}`);
    if (passage.wordCount) bylineParts.push(`${passage.wordCount} words`);
  }

  // ────────────────────────────────────────────
  // Para flash
  // ────────────────────────────────────────────
  const flashPara = useCallback((paraIdx: number) => {
    const bodyEl = passageBodyRef.current;
    const passageEl = passageRef.current;
    if (!bodyEl || !passageEl) return;
    const ps = passageEl.querySelectorAll('p');
    const p = ps[paraIdx];
    if (!p) return;
    if (bodyEl.scrollHeight > bodyEl.clientHeight) {
      bodyEl.scrollTo({ top: Math.max(0, (p as HTMLElement).offsetTop - 90), behavior: 'smooth' });
    }
    p.classList.remove('is-flash');
    void (p as HTMLElement).offsetWidth;
    p.classList.add('is-flash');
    setTimeout(() => p.classList.remove('is-flash'), 1800);
  }, []);

  // ────────────────────────────────────────────
  // TOAST
  // ────────────────────────────────────────────
  const showToast = useCallback((msg: string) => {
    setToastMsg(msg);
    setToastVisible(true);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastVisible(false), 2200);
  }, []);

  // ────────────────────────────────────────────
  // PICK / FLAG
  // ────────────────────────────────────────────
  const pick = useCallback((qi: number, optionId: string) => {
    setRCState(prev => {
      const next = { ...prev, answers: [...prev.answers] };
      next.answers[qi] = optionId;
      persistState(next);
      return next;
    });
  }, [persistState]);

  const toggleFlag = useCallback((qi: number) => {
    setRCState(prev => {
      const next = { ...prev, flags: [...prev.flags] };
      next.flags[qi] = !next.flags[qi];
      persistState(next);
      showToast(next.flags[qi] ? 'Flagged for review' : 'Flag removed');
      return next;
    });
  }, [persistState, showToast]);

  // ────────────────────────────────────────────
  // SCROLL TO QUESTION
  // ────────────────────────────────────────────
  const setCurrent = useCallback((qi: number) => {
    setRCState(prev => {
      if (prev.cur === qi) return prev;
      const next = { ...prev, cur: qi };
      persistState(next);
      return next;
    });
    if (cardRefs.current[qi]?.scrollIntoView) {
      window.scrollTo({
        top: (cardRefs.current[qi] as HTMLElement).offsetTop - 150,
        behavior: 'smooth',
      });
    }
  }, [persistState]);

  // ────────────────────────────────────────────
  // INTERSECTION OBSERVER (scroll-driven current)
  // ────────────────────────────────────────────
  useEffect(() => {
    if (!session || cardRefs.current.length === 0) return;

    ioRef.current?.disconnect();
    ioRef.current = new IntersectionObserver(
      (entries) => {
        let best: IntersectionObserverEntry | null = null;
        entries.forEach(e => {
          if (e.isIntersecting) {
            if (!best || e.boundingClientRect.top < best.boundingClientRect.top) best = e;
          }
        });
        if (best) {
          const qi = parseInt((best as IntersectionObserverEntry).target.getAttribute('data-qi') || '0', 10);
          setRCState(prev => {
            if (prev.cur === qi) return prev;
            const next = { ...prev, cur: qi };
            persistState(next);
            return next;
          });
        }
      },
      { rootMargin: '-200px 0px -45% 0px', threshold: 0.01 }
    );
    cardRefs.current.forEach(c => { if (c) ioRef.current!.observe(c); });
    return () => { ioRef.current?.disconnect(); };
  }, [session, persistState]);

  // ────────────────────────────────────────────
  // TIMER
  // ────────────────────────────────────────────
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // We need to trigger submit from within the timer — use a ref to avoid stale closure
  const submitRef = useRef<((auto?: boolean) => void) | null>(null);

  useEffect(() => {
    if (!session || rcState.done || rcState.seconds <= 0 || rcState.answers.length === 0) return;

    timerRef.current = setInterval(() => {
      setRCState(prev => {
        if (prev.done) return prev;
        if (prev.seconds <= 0) {
          if (timerRef.current) clearInterval(timerRef.current);
          submitRef.current?.(true);
          return prev;
        }
        const next = { ...prev, seconds: prev.seconds - 1 };
        if (next.seconds % 5 === 0) persistState(next);
        return next;
      });
    }, 1000);

    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, rcState.done, rcState.answers.length === 0]);

  // ────────────────────────────────────────────
  // SUBMIT
  // ────────────────────────────────────────────
  const submit = useCallback(async (auto?: boolean) => {
    if (!session || !currentUser?.id) return;
    const total = session.questions.length;
    const cur = saveStateRef.current;
    const answered = cur.answers.filter(a => a !== null).length;
    const unanswered = total - answered;
    if (!auto && unanswered > 0 && !cur.done) {
      const ok = await confirm({
        title: 'Submit with unanswered questions?',
        body: `${unanswered} question${unanswered > 1 ? 's are' : ' is'} still unanswered.`,
        confirmLabel: 'Submit anyway',
        cancelLabel: 'Keep answering',
      });
      if (!ok) return;
    }

    setRCState(prev => {
      const next = { ...prev, done: true };
      persistState(next);
      return next;
    });
    if (timerRef.current) clearInterval(timerRef.current);
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const answersArray = session.questions.map(q => ({
        questionId: q.id,
        answer: saveStateRef.current.answers[session.questions.indexOf(q)] ?? '',
        timeSpent: 0,
      }));

      const startTime = testStartTimeRef.current?.toISOString() ?? new Date().toISOString();

      const res = await axios.post(
        `${API_BASE_URL}/api/students/${currentUser.id}/tests/${testId}/submit`,
        {
          sessionId: session.sessionId,
          answers: answersArray,
          startTime,
        },
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: false,
        }
      );

      if (res.data.success) {
        const data = res.data.data;
        const timeLimit = session.timeLimit;
        const secondsLeft = saveStateRef.current.seconds;
        const timeUsed = timeLimit - secondsLeft;
        setResult({
          score: data.score?.percentage ?? 0,
          correct: data.score?.correct ?? 0,
          total: data.score?.total ?? total,
          timeUsed,
        });
        // Clear local storage after successful submit
        localStorage.removeItem(LS_KEY);
      } else {
        setSubmitError(res.data.error || 'Failed to submit');
      }
    } catch (err: any) {
      setSubmitError(err.response?.data?.error || err.message || 'Failed to submit');
    } finally {
      setIsSubmitting(false);
    }
  }, [session, currentUser?.id, testId, API_BASE_URL, LS_KEY, persistState, confirm]);

  // Wire submit to the ref so the timer can call it
  useEffect(() => { submitRef.current = submit; }, [submit]);

  // ────────────────────────────────────────────
  // KEYBOARD SHORTCUTS
  // ────────────────────────────────────────────
  useEffect(() => {
    if (!session) return;
    const handler = (e: KeyboardEvent) => {
      if (result) return; // overlay open
      if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA') return;
      const total = session.questions.length;
      if (e.key === 'ArrowRight') { e.preventDefault(); setCurrent(Math.min(total - 1, rcState.cur + 1)); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); setCurrent(Math.max(0, rcState.cur - 1)); }
      else if (['1','2','3','4'].includes(e.key)) {
        const idx = +e.key - 1;
        const q = session.questions[rcState.cur];
        const opts = getOpts(q);
        if (opts[idx]) pick(rcState.cur, opts[idx].id);
      } else if (['a','b','c','d','A','B','C','D'].includes(e.key)) {
        const idx = 'abcd'.indexOf(e.key.toLowerCase());
        const q = session.questions[rcState.cur];
        const opts = getOpts(q);
        if (opts[idx]) pick(rcState.cur, opts[idx].id);
      } else if (e.key.toLowerCase() === 'f') {
        toggleFlag(rcState.cur);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, rcState.cur, result, pick, toggleFlag, setCurrent]);

  // ────────────────────────────────────────────
  // HELPERS
  // ────────────────────────────────────────────

  interface NormOption { id: string; text: string; letter: string; }

  const getOpts = (q: Question): NormOption[] => {
    const letters = 'ABCD';
    if (q.content?.options && q.content.options.length > 0) {
      return q.content.options.map((o, i) => ({
        id: o.id ?? o.value ?? String(i),
        text: o.text,
        letter: letters[i] || String(i + 1),
      }));
    }
    if (q.answers && q.answers.length > 0) {
      return q.answers.map((a, i) => ({
        id: a.value ?? String(i),
        text: a.text,
        letter: letters[i] || String(i + 1),
      }));
    }
    return [];
  };

  const getSkillTag = (q: Question) =>
    q.source_metadata?.comprehensionSkill
      ? q.source_metadata.comprehensionSkill.charAt(0).toUpperCase() + q.source_metadata.comprehensionSkill.slice(1)
      : 'Comprehension';

  const getDiffKey = (q: Question): string => {
    const d = (q.difficulty || 'medium').toLowerCase();
    return ['easy','medium','hard'].includes(d) ? d : 'medium';
  };

  // ────────────────────────────────────────────
  // RESULT DIALOG: title/sub based on score
  // ────────────────────────────────────────────
  const getResultText = (pct: number) => {
    const firstName = (currentUser?.name || '').split(' ')[0] || '';
    if (pct >= 80) return {
      title: firstName ? `Brilliant reading, ${firstName}!` : 'Brilliant reading!',
      sub: 'Top-band comprehension. You really understood that passage.',
    };
    if (pct >= 50) return {
      title: firstName ? `Nice reading, ${firstName}!` : 'Nice reading!',
      sub: "Solid effort — let's revisit the tricky questions together.",
    };
    return {
      title: firstName ? `Good effort, ${firstName}!` : 'Good effort!',
      sub: "Re-reading the passage is a superpower. Let's debrief the misses.",
    };
  };

  // ────────────────────────────────────────────
  // TIMER RING
  // ────────────────────────────────────────────
  const timerClass = [
    'rc-timer',
    rcState.seconds <= 60 ? 'is-crit' : rcState.seconds <= 5 * 60 ? 'is-warn' : '',
  ].filter(Boolean).join(' ');

  const timeLimit = session?.timeLimit ?? 1;
  const frac = Math.max(0, rcState.seconds / timeLimit);
  const ringOffset = RING_LEN * (1 - frac);

  // ────────────────────────────────────────────
  // COMPUTED TRACKER STATS
  // ────────────────────────────────────────────
  const answeredCount = rcState.answers.filter(a => a !== null).length;
  const flaggedCount = rcState.flags.filter(Boolean).length;
  const pct = session ? Math.round((answeredCount / session.questions.length) * 100) : 0;

  // ────────────────────────────────────────────
  // LOADING / ERROR STATES
  // ────────────────────────────────────────────
  if (loading) {
    return (
      <div className="rc-root">
        <div className="rc-loading">
          <div className="rc-loading__spinner" />
          <span className="rc-loading__text">Loading reading challenge…</span>
        </div>
      </div>
    );
  }

  if (loadError || !session) {
    return (
      <div className="rc-root">
        <div className="rc-error">
          <h2>Couldn't load the test</h2>
          <p>{loadError || 'Unknown error'}</p>
          <button className="rc-btn rc-btn--ghost" onClick={onCancel}>Back to dashboard</button>
        </div>
      </div>
    );
  }

  const total = session.questions.length;

  // ────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────
  return (
    <div className="rc-root">

      {/* ── APP BAR ── */}
      <header className="rc-bar">
        <div className="rc-bar__inner">
          <span className="rc-bar__brand">
            <img src={logoUrl} alt="Cleveroo" />
            <b>cleve<span className="rc-r">r</span><span className="rc-o">oo</span></b>
          </span>
          <div className="rc-bar__divider" />
          <div className="rc-bar__test">
            <h1>{session.test.title}</h1>
            <small>Reading challenge · auto-saved as you go</small>
          </div>
          <div className="rc-bar__right">
            <div className={timerClass}>
              <svg className="rc-timer__ring" viewBox="0 0 26 26">
                <circle className="rc-trk" cx="13" cy="13" r="11" />
                <circle
                  className="rc-val"
                  cx="13" cy="13" r="11"
                  style={{
                    strokeDasharray: RING_LEN,
                    strokeDashoffset: ringOffset,
                  }}
                />
              </svg>
              <span className="rc-timer__txt">
                <span className="rc-timer__num">{fmtTime(rcState.seconds)}</span>
                <span className="rc-timer__lbl">remaining</span>
              </span>
            </div>
            <SoundMuteToggle />
            <button
              className="rc-bar__exit"
              onClick={async () => {
                if (await confirm({
                  title: 'Exit the reading challenge?',
                  body: 'Your answers are saved — you can come back and finish later.',
                  confirmLabel: 'Exit',
                  cancelLabel: 'Keep going',
                })) {
                  onCancel();
                }
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <path d="m16 17 5-5-5-5"/>
                <path d="M21 12H9"/>
              </svg>
              Exit
            </button>
          </div>
        </div>
      </header>

      {/* ── MAIN SHELL ── */}
      <main className="rc-shell">

        {/* LEFT: PASSAGE */}
        <aside className="rc-reading">
          <div className="rc-reading__head">
            <div className="rc-reading__eyebrow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 6s3-1.5 6-1.5S14 6 14 6v13s-3-1.5-6-1.5S2 19 2 19z"/>
                <path d="M14 6s3-1.5 6-1.5S22 6 22 6v13s-1-.5-2-.8"/>
              </svg>
              Read the passage
            </div>
            {passage?.title && <h2 className="rc-reading__title">{passage.title}</h2>}
            {bylineParts.length > 0 && (
              <p className="rc-reading__byline">
                {bylineParts.map((part, i) => {
                  if (part.startsWith('by ')) {
                    return (
                      <React.Fragment key={i}>
                        {i > 0 && ' · '}
                        by <b>{part.slice(3)}</b>
                      </React.Fragment>
                    );
                  }
                  return <React.Fragment key={i}>{i > 0 && ' · '}{part}</React.Fragment>;
                })}
              </p>
            )}
          </div>

          <div className="rc-reading__body" ref={passageBodyRef}>
            {passageIsHtml ? (
              <div
                className="rc-passage rc-passage-html"
                ref={passageRef}
                dangerouslySetInnerHTML={{ __html: passageBody }}
              />
            ) : (
              <div className="rc-passage" ref={passageRef}>
                {passageParas.map((para, i) => (
                  <p key={i}>
                    <span className="rc-pn">{i + 1}</span>
                    {para}
                  </p>
                ))}
              </div>
            )}
          </div>

          <div className="rc-reading__foot">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 8v4l3 2"/>
              <circle cx="12" cy="12" r="9"/>
            </svg>
            Tip: tap a question's reference to jump to the right paragraph.
          </div>
        </aside>

        {/* RIGHT: QUESTIONS */}
        <section className="rc-qcol">

          {/* Sticky tracker */}
          <div className="rc-tracker">
            <div className="rc-tracker__top">
              <span className="rc-tracker__ico">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 18l5-6 4 4 8-10"/>
                </svg>
              </span>
              <span className="rc-tracker__lbl">
                Answered <b>{answeredCount}</b> of <span>{total}</span>
              </span>
              {flaggedCount > 0 && (
                <span className="rc-tracker__flag">
                  <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
                    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
                    <line x1="4" y1="22" x2="4" y2="15"/>
                  </svg>
                  {flaggedCount}
                </span>
              )}
              <span className="rc-tracker__pct">{pct}%</span>
            </div>
            <div className="rc-pbar">
              <div className="rc-pbar__fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="rc-tracker__map">
              {session.questions.map((_, i) => {
                const isAnswered = rcState.answers[i] !== null;
                const isCurrent = i === rcState.cur;
                const isFlagged = rcState.flags[i];
                let chipCls = 'rc-chip';
                if (isCurrent) chipCls += ' is-current';
                else if (isAnswered) chipCls += ' is-answered';
                if (isFlagged) chipCls += ' is-flagged';
                return (
                  <button key={i} className={chipCls} onClick={() => setCurrent(i)}>
                    {i + 1}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Question cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {session.questions.map((q, qi) => {
              const opts = getOpts(q);
              const pickedId = rcState.answers[qi];
              const isCurrent = qi === rcState.cur;
              const isAnswered = pickedId !== null;
              const isFlagged = rcState.flags[qi];
              const diffKey = getDiffKey(q);
              const skillTag = getSkillTag(q);
              const paraRef = q.content?.passageParagraph;
              const qText = q.content?.questionText || q.content?.question || q.title;

              let cardCls = 'rc-qcard';
              if (isCurrent) cardCls += ' is-current';
              if (isAnswered) cardCls += ' is-answered';

              return (
                <section
                  key={q.id}
                  className={cardCls}
                  id={`rc-q${qi}`}
                  data-qi={qi}
                  ref={el => { cardRefs.current[qi] = el; }}
                >
                  <div className="rc-qcard__top">
                    {/* Skill tag */}
                    <span className="rc-tag rc-tag--type">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                      </svg>
                      {skillTag}
                    </span>
                    {/* Difficulty tag */}
                    <span className={`rc-tag ${DIFF_CLASS[diffKey]}`}>
                      <span className="rc-tag__diffdot" />
                      {DIFF_LABEL[diffKey]}
                    </span>
                    {/* Para ref chip — only if we have a paragraph index */}
                    {paraRef != null && (
                      <button
                        className="rc-qcard__ref"
                        onClick={() => flashPara(paraRef - 1)}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M7 7h10M7 12h10M7 17h6"/>
                        </svg>
                        Para {paraRef}
                      </button>
                    )}
                    {/* Q number */}
                    <span className="rc-qcard__qno">Q<b>{qi + 1}</b>/{total}</span>
                  </div>

                  {/* Question text */}
                  <p
                    className="rc-qcard__q"
                    dangerouslySetInnerHTML={{ __html: qText || '' }}
                  />

                  {/* Options */}
                  <div className="rc-opts">
                    {opts.map((opt) => {
                      const isPicked = pickedId === opt.id;
                      return (
                        <button
                          key={opt.id}
                          className={`rc-opt${isPicked ? ' is-picked' : ''}`}
                          onMouseEnter={playHover}
                          onFocus={playHover}
                          onClick={() => { playSelect(); pick(qi, opt.id); }}
                        >
                          <span className="rc-opt__tag">{opt.letter}</span>
                          <span className="rc-opt__text">{opt.text}</span>
                          <span className="rc-opt__check">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 6 9 17l-5-5"/>
                            </svg>
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Card footer */}
                  <div className="rc-qcard__foot">
                    <button
                      className={`rc-linkbtn rc-flagbtn${isFlagged ? ' is-on' : ''}`}
                      onClick={() => toggleFlag(qi)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
                        <line x1="4" y1="22" x2="4" y2="15"/>
                      </svg>
                      {isFlagged ? 'Flagged for review' : 'Flag for review'}
                    </button>
                    <span className="rc-qcard__saved">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6 9 17l-5-5"/>
                      </svg>
                      Answer saved
                    </span>
                  </div>
                </section>
              );
            })}
          </div>

          {/* Finish card */}
          <div className="rc-finish">
            <div className="rc-finish__txt">
              <h3>Finished reading and answering?</h3>
              <p>You can review any question using the map above before you submit.</p>
            </div>
            <button
              className="rc-btn rc-btn--submit"
              onClick={() => submit()}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting…' : 'Submit challenge'}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5"/>
              </svg>
            </button>
          </div>

          {submitError && (
            <div style={{ color: '#E26B4F', fontWeight: 600, fontSize: 14, padding: '8px 0' }}>
              {submitError}
            </div>
          )}

        </section>
      </main>

      {/* ── STICKY NAV ── */}
      <nav className="rc-navbar">
        <div className="rc-navbar__inner">
          <button
            className="rc-btn rc-btn--ghost"
            disabled={rcState.cur === 0}
            onClick={() => setCurrent(Math.max(0, rcState.cur - 1))}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5m6-6-6 6 6 6"/>
            </svg>
            <span className="lbl">Previous</span>
          </button>
          <div className="rc-navbar__mid">
            <span className="rc-navbar__ind">
              Question <b>{rcState.cur + 1}</b> of <span>{total}</span>
            </span>
            <div className="rc-navbar__dots">
              {session.questions.map((_, i) => {
                const isAnswered = rcState.answers[i] !== null;
                const isCurrent = i === rcState.cur;
                const isFlagged = rcState.flags[i];
                let dotCls = 'rc-navbar__dot';
                if (isCurrent) dotCls += ' is-current';
                else if (isAnswered) dotCls += ' is-answered';
                else if (isFlagged) dotCls += ' is-flagged';
                return <span key={i} className={dotCls} />;
              })}
            </div>
          </div>
          <button
            className="rc-btn rc-btn--primary"
            disabled={rcState.cur === total - 1}
            onClick={() => setCurrent(Math.min(total - 1, rcState.cur + 1))}
          >
            <span className="lbl">Next</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14m-6-6 6 6-6 6"/>
            </svg>
          </button>
        </div>
      </nav>

      {/* ── RESULTS OVERLAY ── */}
      {result && (() => {
        const { title, sub } = getResultText(result.score);
        return (
          <div className="rc-scrim is-open">
            <div className="rc-dialog">
              <div className="rc-dialog__badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9V6a3 3 0 0 1 3-3h6a3 3 0 0 1 3 3v3"/>
                  <path d="M4 9h16v3a8 8 0 1 1-16 0z"/>
                  <path d="M12 17v4M8 21h8"/>
                </svg>
              </div>
              <h2>{title}</h2>
              <p>{sub}</p>
              <div className="rc-dialog__score">
                <div className="rc-scorebox">
                  <b>{result.score}%</b>
                  <small>Score</small>
                </div>
                <div className="rc-scorebox alt">
                  <b>{result.correct}/{result.total}</b>
                  <small>Correct</small>
                </div>
                <div className="rc-scorebox alt2">
                  <b>{fmtTime(result.timeUsed)}</b>
                  <small>Time used</small>
                </div>
              </div>
              <div className="rc-dialog__actions">
                <button
                  className="rc-btn rc-btn--ghost"
                  onClick={() => {
                    setResult(null);
                    setCurrent(0);
                  }}
                >
                  Review answers
                </button>
                <button className="rc-btn rc-btn--primary" onClick={onCancel}>
                  Back to dashboard
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── TOAST ── */}
      <div className={`rc-toast${toastVisible ? ' is-show' : ''}`}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6 9 17l-5-5"/>
        </svg>
        <span>{toastMsg}</span>
      </div>

      {confirmDialog}
    </div>
  );
};

export default ReadingChallenge;
